import {
  Box,
  Button,
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputLabel,
  MenuItem,
  Modal,
  Select,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import React from "react";
import {
  BUTTON_STYLES,
  FORM_STYLES,
  MODAL_STYLES,
  TEXT_FIELD_STYLES,
} from "../../styles/componentStyles";
import { useFormik } from "formik";
import * as Yup from "yup";
import api from "../../axiosInstance";
import { toast } from "react-toastify";

const buildValidationSchema = (isEdit) =>
  Yup.object({
    accountHolder: Yup.string().required("Account holder name is required"),
    bankName: Yup.string().required("Bank name is required"),
    accountNumber: isEdit
      ? Yup.string().matches(
          /^[0-9]{9,18}$/,
          "Account number must be 9–18 digits",
        )
      : Yup.string()
          .matches(/^[0-9]{9,18}$/, "Account number must be 9–18 digits")
          .required("Account number is required"),
    ifsc: Yup.string()
      .matches(/^[A-Z]{4}0[A-Z0-9]{6}$/, "Invalid IFSC code")
      .required("IFSC code is required"),
    branch: Yup.string().required("Branch name is required"),
    accountType: Yup.string().required("Account type is required"),
  });

function BankDetailsModal({
  open,
  onClose,
  mentorProfileId,
  mentorId,
  bankDetailsEdit,
  onSuccess,
}) {
  const isEdit = Boolean(bankDetailsEdit?.id);

  const formik = useFormik({
    initialValues: {
      accountHolder: bankDetailsEdit?.accountHolder || "",
      bankName: bankDetailsEdit?.bankName || "",
      accountNumber: "",
      ifsc: bankDetailsEdit?.ifsc || "",
      branch: bankDetailsEdit?.branch || "",
      accountType: bankDetailsEdit?.accountType || "Savings",
      isDefault: bankDetailsEdit?.isDefault ?? false,
    },
    validationSchema: buildValidationSchema(isEdit),
    enableReinitialize: true,
    onSubmit: async (values, { resetForm, setSubmitting }) => {
      if (!mentorProfileId) {
        toast.error("Missing mentor profile id.");
        return;
      }
      const payload = { ...values, mentorId };
      // Leaving Account Number blank on edit means "keep the existing number"
      // — the GET response only ever returns a masked value, so there's
      // nothing real to prefill or diff against.
      if (isEdit && !payload.accountNumber) delete payload.accountNumber;
      try {
        setSubmitting(true);
        const response = isEdit
          ? await api.put(
              `/admin/mentors/${mentorProfileId}/bank-accounts/${bankDetailsEdit.id}`,
              payload,
            )
          : await api.post(
              `/admin/mentors/${mentorProfileId}/bank-accounts`,
              payload,
            );
        toast.success(
          `Bank details ${isEdit ? "updated" : "added"} successfully!`,
          { position: "top-right", autoClose: 2000 },
        );
        onSuccess?.(response.data?.data);
        resetForm();
        onClose();
      } catch (error) {
        toast.error(
          error.response?.data?.errorMessage ||
            "Failed to save bank details. Please try again.",
          { autoClose: 3000 },
        );
      } finally {
        setSubmitting(false);
      }
    },
  });

  return (
    <Modal open={open} onClose={onClose} disableScrollLock>
      <Box
        sx={{ ...MODAL_STYLES.standard, width: { xs: 340, md: 500 } }}
        component="form"
        onSubmit={formik.handleSubmit}
      >
        <Typography variant="h5" component="h2" sx={{ mb: 3, fontWeight: 600 }}>
          {isEdit ? "Update Bank Details" : "Add Bank Details"}
        </Typography>
        <Box sx={FORM_STYLES.container}>
          <TextField
            label="Account Holder Name"
            name="accountHolder"
            value={formik.values.accountHolder}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={
              formik.touched.accountHolder &&
              Boolean(formik.errors.accountHolder)
            }
            helperText={
              formik.touched.accountHolder && formik.errors.accountHolder
            }
            {...TEXT_FIELD_STYLES.standard}
          />
          <TextField
            label="Bank Name"
            name="bankName"
            value={formik.values.bankName}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.bankName && Boolean(formik.errors.bankName)}
            helperText={formik.touched.bankName && formik.errors.bankName}
            {...TEXT_FIELD_STYLES.standard}
          />
          <TextField
            label="Account Number"
            name="accountNumber"
            placeholder={
              isEdit
                ? `Current: ${bankDetailsEdit?.accountNumberMasked || "••••"} (leave blank to keep)`
                : ""
            }
            value={formik.values.accountNumber}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={
              formik.touched.accountNumber &&
              Boolean(formik.errors.accountNumber)
            }
            helperText={
              (formik.touched.accountNumber && formik.errors.accountNumber) ||
              (isEdit ? "Leave blank to keep the existing account number" : "")
            }
            {...TEXT_FIELD_STYLES.standard}
          />
          <TextField
            label="IFSC Code"
            name="ifsc"
            value={formik.values.ifsc}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.ifsc && Boolean(formik.errors.ifsc)}
            helperText={formik.touched.ifsc && formik.errors.ifsc}
            {...TEXT_FIELD_STYLES.standard}
          />
          <TextField
            label="Branch Name"
            name="branch"
            value={formik.values.branch}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.branch && Boolean(formik.errors.branch)}
            helperText={formik.touched.branch && formik.errors.branch}
            {...TEXT_FIELD_STYLES.standard}
          />
          <FormControl
            fullWidth
            sx={{ mt: 2 }}
            error={
              formik.touched.accountType && Boolean(formik.errors.accountType)
            }
          >
            <InputLabel>Account Type</InputLabel>
            <Select
              name="accountType"
              value={formik.values.accountType}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              label="Account Type"
            >
              <MenuItem value="Savings">Savings</MenuItem>
              <MenuItem value="Current">Current</MenuItem>
              <MenuItem value="Salary">Salary</MenuItem>
            </Select>
            {formik.touched.accountType && formik.errors.accountType && (
              <FormHelperText>{formik.errors.accountType}</FormHelperText>
            )}
          </FormControl>

          <FormControlLabel
            sx={{ mt: 1 }}
            control={
              <Switch
                name="isDefault"
                checked={formik.values.isDefault}
                onChange={(e) =>
                  formik.setFieldValue("isDefault", e.target.checked)
                }
              />
            }
            label="Set as default bank account"
          />

          <Box
            sx={FORM_STYLES.actions}
            style={{
              display: "flex",
              marginTop: "20px",
              justifyContent: "flex-end",
              gap: "10px",
            }}
          >
            <Button onClick={onClose} {...BUTTON_STYLES.secondary}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={formik.isSubmitting}
              {...BUTTON_STYLES.primary}
            >
              {formik.isSubmitting ? "Processing..." : "Save"}
            </Button>
          </Box>
        </Box>
      </Box>
    </Modal>
  );
}

export default BankDetailsModal;
